import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";

import { useAuth } from "@/context/AuthContext";
import useSiteSettings from "@/hooks/useSiteSettings";
import { siteApi } from "@/lib/api";
import { normalizeBackendRichText } from "@/utils/backendRichText";

import {
  buildMonthCalendarDays,
  formatMonthLabel,
  getAttendanceResultMessage,
  getAttendanceRowDateKey,
  getKoreanDateKey,
  normalizeAttendanceRows,
} from "./attendance.helpers";

const WEEKDAYS = ["일", "월", "화", "수", "목", "금", "토"];

const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M7 2.75a.75.75 0 0 1 .75.75v1h8.5v-1a.75.75 0 0 1 1.5 0v1h1A3.25 3.25 0 0 1 22 7.75v9A3.25 3.25 0 0 1 18.75 20h-13.5A3.25 3.25 0 0 1 2 16.75v-9A3.25 3.25 0 0 1 5.25 4.5h1v-1A.75.75 0 0 1 7 2.75ZM3.5 9v7.75a1.75 1.75 0 0 0 1.75 1.75h13.5a1.75 1.75 0 0 0 1.75-1.75V9h-17Zm1.75-3A1.75 1.75 0 0 0 3.5 7.75V7.8h17v-.05A1.75 1.75 0 0 0 18.75 6h-13.5Zm11.78 5.72a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0l-2.25-2.25a.75.75 0 0 1 1.06-1.06l1.72 1.72 3.72-3.72a.75.75 0 0 1 1.06 0Z" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M9.55 16.62a1 1 0 0 1-.7-.3l-3.2-3.2a1 1 0 1 1 1.4-1.42l2.5 2.49 7.4-7.4a1 1 0 0 1 1.42 1.42l-8.1 8.1a1 1 0 0 1-.72.31Z" />
  </svg>
);

const XIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M6.22 4.81a1 1 0 0 0-1.41 1.41L10.59 12l-5.78 5.78a1 1 0 1 0 1.41 1.41L12 13.41l5.78 5.78a1 1 0 0 0 1.41-1.41L13.41 12l5.78-5.78a1 1 0 0 0-1.41-1.41L12 10.59 6.22 4.81Z" />
  </svg>
);

const NoticeModal = ({ dialog, onClose }) => {
  if (!dialog) return null;

  return (
    <div className="attendance-alert-backdrop" role="presentation">
      <div className={`attendance-alert ${dialog.type || "info"}`} role="alertdialog" aria-modal="true">
        <strong>{dialog.title}</strong>
        <p>{dialog.message}</p>
        <button type="button" onClick={onClose}>
          확인
        </button>
      </div>
    </div>
  );
};

const AttendanceCheckPage = ({ onRequestClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoading: isAuthLoading, refreshProfile } = useAuth();
  const { settings } = useSiteSettings();
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isChecking, setIsChecking] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [isNoticeOpen, setIsNoticeOpen] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [monthDate, setMonthDate] = useState(() => new Date());
  const [isClosing, setIsClosing] = useState(false);
  const closeTimerRef = useRef(null);

  const isCheckingAuth = Boolean(isAuthLoading || user?.isLoading);
  const isAuthenticated = Boolean(user?.isAuthenticated);
  const todayKey = useMemo(() => getKoreanDateKey(), []);
  const attendanceEnabled = settings?.use_attendance_check !== false;
  const manualCheckEnabled = settings?.is_attandance_manualy !== false;

  const noticeHtml = useMemo(
    () =>
      normalizeBackendRichText(
        settings?.attandance_notice ||
          settings?.attendance_notice ||
          settings?.attandance_info ||
          settings?.attendance_info ||
          ""
      ),
    [settings]
  );

  const attendanceDateSet = useMemo(() => {
    const keys = rows.map(getAttendanceRowDateKey).filter(Boolean);
    return new Set(keys);
  }, [rows]);

  const checkedToday = attendanceDateSet.has(todayKey);
  const calendarDays = useMemo(() => buildMonthCalendarDays(monthDate), [monthDate]);

  const finishClose = useCallback(() => {
    if (typeof onRequestClose === "function") {
      onRequestClose();
      return;
    }

    if (location.key !== "default" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/");
  }, [location.key, navigate, onRequestClose]);

  const requestClose = useCallback(() => {
    if (isClosing) return;

    setIsClosing(true);
    closeTimerRef.current = window.setTimeout(finishClose, 180);
  }, [finishClose, isClosing]);

  const showDialog = useCallback((type, title, message) => {
    setDialog({ type, title, message });
  }, []);

  const fetchAttendance = useCallback(async () => {
    if (isCheckingAuth) return;

    if (!isAuthenticated) {
      setRows([]);
      setErrorText("로그인 후 출석체크를 이용할 수 있습니다.");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const result = await siteApi.getAttendance({ limit: "ALL" });

    if (result?.success) {
      setRows(normalizeAttendanceRows(result));
      setErrorText("");
    } else {
      setRows([]);
      setErrorText(result?.error || result?.message || "출석 현황을 불러오지 못했습니다.");
    }

    setIsLoading(false);
  }, [isAuthenticated, isCheckingAuth]);

  useEffect(() => {
    if (isCheckingAuth) {
      setIsLoading(true);
      return;
    }

    fetchAttendance();
  }, [fetchAttendance, isCheckingAuth]);

  useEffect(
    () => () => {
      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current);
      }
    },
    []
  );

  const handleMonthMove = (direction) => {
    setMonthDate((current) => new Date(current.getFullYear(), current.getMonth() + direction, 1));
  };

  const handleCheckAttendance = async () => {
    if (isCheckingAuth) return;

    if (!isAuthenticated) {
      showDialog("warning", "로그인이 필요합니다", "로그인 후 출석체크를 이용해주세요.");
      return;
    }

    if (!attendanceEnabled || !manualCheckEnabled) {
      showDialog("warning", "출석체크 안내", "현재 출석체크를 이용할 수 없습니다.");
      return;
    }

    if (checkedToday) {
      showDialog("success", "출석완료", "오늘 출석체크가 이미 완료되었습니다.");
      return;
    }

    setIsChecking(true);
    const result = await siteApi.checkAttendance();
    const message = getAttendanceResultMessage(result);

    if (result?.success) {
      showDialog("success", "출석완료", message || "출석체크가 완료되었습니다.");
      await fetchAttendance();
      if (typeof refreshProfile === "function") {
        refreshProfile();
      }
    } else {
      showDialog("warning", "출석체크 실패", message || "출석체크를 처리하지 못했습니다.");
    }

    setIsChecking(false);
  };

  const actionText = (() => {
    if (isCheckingAuth || isLoading) return "확인 중";
    if (!isAuthenticated) return "로그인 필요";
    if (checkedToday) return "출석완료";
    if (isChecking) return "처리 중";
    return "출석체크";
  })();

  return (
    <AttendanceOverlay className={isClosing ? "is-closing" : ""}>
      <div className="attendance-backdrop" role="presentation" onClick={requestClose} />
      <section className="attendance-panel" role="dialog" aria-modal="true" aria-labelledby="attendance-title">
        <header className="attendance-panel-header">
          <div className="attendance-title-group">
            <span className="attendance-title-icon">
              <CalendarIcon />
            </span>
            <div>
              <p>DAILY CHECK</p>
              <h1 id="attendance-title">출석체크</h1>
            </div>
          </div>
          <button type="button" className="attendance-close" aria-label="닫기" onClick={requestClose}>
            <XIcon />
          </button>
        </header>

        <div className="attendance-panel-body">
          <div className="attendance-status-card">
            <div>
              <span className="status-label">오늘 출석상태</span>
              <strong className={checkedToday ? "is-checked" : ""}>
                {checkedToday ? "출석완료" : "오늘 미출석"}
              </strong>
            </div>
            <button
              type="button"
              className="attendance-check-button"
              onClick={handleCheckAttendance}
              disabled={isChecking || isCheckingAuth || isLoading || checkedToday}
            >
              {checkedToday ? <CheckIcon /> : null}
              {actionText}
            </button>
          </div>

          {noticeHtml ? (
            <div className="attendance-notice-block">
              <button
                type="button"
                className="attendance-notice-toggle"
                aria-expanded={isNoticeOpen}
                onClick={() => setIsNoticeOpen((value) => !value)}
              >
                안내사항 {isNoticeOpen ? "닫기" : "보기"}
              </button>
              <div className={`attendance-notice-content ${isNoticeOpen ? "open" : ""}`}>
                <div dangerouslySetInnerHTML={{ __html: noticeHtml }} />
              </div>
            </div>
          ) : null}

          <div className="attendance-calendar">
            <div className="calendar-toolbar">
              <button type="button" aria-label="이전 달" onClick={() => handleMonthMove(-1)}>
                ‹
              </button>
              <strong>{formatMonthLabel(monthDate)}</strong>
              <button type="button" aria-label="다음 달" onClick={() => handleMonthMove(1)}>
                ›
              </button>
            </div>

            <div className="calendar-weekdays">
              {WEEKDAYS.map((weekday) => (
                <span key={weekday}>{weekday}</span>
              ))}
            </div>

            <div className="calendar-grid" aria-busy={isLoading}>
              {calendarDays.map((day) => {
                if (day.isEmpty) {
                  return <span key={day.key} className="calendar-day empty" aria-hidden="true" />;
                }

                const isToday = day.key === todayKey;
                const isChecked = attendanceDateSet.has(day.key);

                return (
                  <span
                    key={day.key}
                    className={`calendar-day ${isToday ? "today" : ""} ${isChecked ? "checked" : ""}`}
                    title={isChecked ? `${day.key} 출석완료` : day.key}
                  >
                    <span>{day.day}</span>
                    {isChecked ? <CheckIcon /> : null}
                  </span>
                );
              })}
            </div>
            {!isLoading && errorText ? <p className="attendance-calendar-message">{errorText}</p> : null}
          </div>
        </div>

        <NoticeModal dialog={dialog} onClose={() => setDialog(null)} />
      </section>
    </AttendanceOverlay>
  );
};

const AttendanceOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  color: #f6f8fb;
  line-height: 1.35;
  animation: attendance-overlay-in 0.18s ease both;

  &.is-closing {
    pointer-events: none;
    animation: attendance-overlay-out 0.18s ease both;
  }

  .attendance-panel,
  .attendance-panel * {
    box-sizing: border-box;
  }

  .attendance-backdrop {
    position: absolute;
    inset: 0;
    background: rgba(4, 13, 18, 0.72);
    backdrop-filter: blur(7px);
    animation: attendance-backdrop-in 0.2s ease both;
  }

  .attendance-panel {
    position: relative;
    width: min(760px, 100%);
    max-height: calc(100vh - 36px);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border-radius: 16px;
    background: #10272f;
    border: 1px solid rgba(157, 187, 205, 0.2);
    box-shadow: 0 26px 80px rgba(0, 0, 0, 0.48);
    transform-origin: center;
    animation: attendance-panel-in 0.22s cubic-bezier(0.2, 0.75, 0.2, 1) both;
  }

  &.is-closing .attendance-panel {
    animation: attendance-panel-out 0.18s ease both;
  }

  &.is-closing .attendance-backdrop {
    animation: attendance-backdrop-out 0.18s ease both;
  }

  .attendance-panel-header {
    min-height: 74px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 18px 22px;
    background: #0d2028;
    border-bottom: 1px solid rgba(157, 187, 205, 0.14);
  }

  .attendance-title-group {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .attendance-title-icon {
    width: 42px;
    height: 42px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    color: var(--crypto-green);
    background: rgba(74, 244, 119, 0.12);
    border: 1px solid rgba(74, 244, 119, 0.24);
  }

  .attendance-title-icon svg,
  .attendance-close svg,
  .attendance-check-button svg,
  .calendar-day svg {
    width: 19px;
    height: 19px;
    fill: currentColor;
  }

  .attendance-title-group p {
    margin: 0 0 3px;
    color: var(--crypto-green);
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0;
  }

  h1 {
    margin: 0;
    color: #fff;
    font-size: 28px;
    font-weight: 800;
    line-height: 1;
  }

  .attendance-close {
    width: 42px;
    height: 42px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    color: #bfd0dc;
    background: rgba(157, 187, 205, 0.1);
    transition: background 0.18s ease, color 0.18s ease, transform 0.18s ease;
  }

  .attendance-close:hover {
    color: #fff;
    background: rgba(157, 187, 205, 0.18);
    transform: translateY(-1px);
  }

  .attendance-panel-body {
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-height: 0;
    overflow-y: auto;
    padding: 16px 22px 20px;
    scrollbar-width: thin;
    scrollbar-color: rgba(74, 244, 119, 0.55) rgba(157, 187, 205, 0.08);
  }

  .attendance-panel-body::-webkit-scrollbar {
    width: 7px;
  }

  .attendance-panel-body::-webkit-scrollbar-thumb {
    border-radius: 999px;
    background: rgba(74, 244, 119, 0.55);
  }

  .attendance-panel-body::-webkit-scrollbar-track {
    background: rgba(157, 187, 205, 0.08);
  }

  .attendance-status-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    padding: 14px 16px;
    border-radius: 12px;
    background: #0b1c24;
    border: 1px solid rgba(157, 187, 205, 0.18);
  }

  .status-label {
    display: block;
    margin-bottom: 4px;
    color: #9fb2c1;
    font-size: 12px;
    font-weight: 700;
  }

  .attendance-status-card strong {
    color: #f6f8fb;
    font-size: 21px;
    font-weight: 800;
  }

  .attendance-status-card strong.is-checked {
    color: var(--crypto-green);
  }

  .attendance-check-button {
    min-width: 122px;
    height: 42px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 0 18px;
    border-radius: 10px;
    color: #07120c;
    background: var(--crypto-green);
    font-size: 14px;
    font-weight: 800;
    box-shadow: 0 10px 28px rgba(74, 244, 119, 0.18);
    transition: filter 0.18s ease, transform 0.18s ease, opacity 0.18s ease;
  }

  .attendance-check-button:hover:not(:disabled) {
    filter: brightness(1.05);
    transform: translateY(-1px);
  }

  .attendance-check-button:disabled {
    cursor: not-allowed;
    opacity: 0.62;
    box-shadow: none;
  }

  .attendance-notice-block {
    border-radius: 12px;
    background: #0b1c24;
    border: 1px solid rgba(157, 187, 205, 0.16);
    overflow: hidden;
  }

  .attendance-notice-toggle {
    width: 100%;
    min-height: 36px;
    height: auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    color: #dce7ee;
    font-size: 13px;
    font-weight: 800;
    line-height: 1.1;
    text-align: left;
  }

  .attendance-notice-content {
    display: grid;
    grid-template-rows: 0fr;
    color: #b9c7d3;
    transition: grid-template-rows 0.24s ease;
  }

  .attendance-notice-content.open {
    grid-template-rows: 1fr;
    border-top: 1px solid rgba(157, 187, 205, 0.12);
  }

  .attendance-notice-content > div {
    min-height: 0;
    overflow: hidden;
    padding: 0 16px;
    line-height: 1.6;
    font-size: 13px;
  }

  .attendance-notice-content.open > div {
    padding: 12px 14px 14px;
  }

  .attendance-notice-content p {
    margin: 0 0 8px;
  }

  .attendance-notice-content p:last-child {
    margin-bottom: 0;
  }

  .attendance-calendar {
    padding: 14px;
    border-radius: 13px;
    background: #0b1c24;
    border: 1px solid rgba(157, 187, 205, 0.18);
  }

  .calendar-toolbar {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    margin-bottom: 12px;
  }

  .calendar-toolbar strong {
    min-width: 110px;
    text-align: center;
    color: #f6f8fb;
    font-size: 16px;
    font-weight: 800;
  }

  .calendar-toolbar button {
    width: 34px;
    height: 34px;
    border-radius: 9px;
    color: #dce7ee;
    background: rgba(157, 187, 205, 0.1);
    font-size: 22px;
    line-height: 1;
  }

  .calendar-weekdays,
  .calendar-grid {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    gap: 6px;
  }

  .calendar-weekdays {
    margin-bottom: 6px;
  }

  .calendar-weekdays span {
    color: #8295a4;
    text-align: center;
    font-size: 11px;
    font-weight: 800;
  }

  .calendar-day {
    position: relative;
    min-height: 42px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    color: #c8d4df;
    background: rgba(157, 187, 205, 0.06);
    border: 1px solid transparent;
    font-size: 13px;
    font-weight: 800;
  }

  .calendar-day.empty {
    background: transparent;
  }

  .calendar-day.today {
    border-color: rgba(74, 244, 119, 0.4);
  }

  .calendar-day.checked {
    color: #07120c;
    background: var(--crypto-green);
    box-shadow: 0 10px 22px rgba(74, 244, 119, 0.16);
  }

  .calendar-day.checked svg {
    position: absolute;
    right: 7px;
    bottom: 7px;
    width: 13px;
    height: 13px;
  }

  .attendance-calendar-message {
    margin: 10px 0 0;
    color: #9fb2c1;
    text-align: center;
    font-size: 12px;
    font-weight: 700;
  }

  .attendance-alert-backdrop {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    background: rgba(4, 13, 18, 0.58);
    z-index: 2;
    animation: attendance-backdrop-in 0.16s ease both;
  }

  .attendance-alert {
    width: min(360px, 100%);
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 22px;
    border-radius: 16px;
    background: #0b1c24;
    border: 1px solid rgba(157, 187, 205, 0.2);
    box-shadow: 0 18px 48px rgba(0, 0, 0, 0.42);
    animation: attendance-alert-in 0.2s cubic-bezier(0.2, 0.75, 0.2, 1) both;
  }

  .attendance-alert.success {
    border-color: rgba(74, 244, 119, 0.36);
  }

  .attendance-alert.warning {
    border-color: rgba(255, 166, 87, 0.4);
  }

  .attendance-alert strong {
    color: #fff;
    font-size: 18px;
    font-weight: 800;
  }

  .attendance-alert p {
    margin: 0;
    color: #b9c7d3;
    font-size: 14px;
    line-height: 1.55;
  }

  .attendance-alert button {
    height: 42px;
    border-radius: 10px;
    color: #07120c;
    background: var(--crypto-green);
    font-size: 14px;
    font-weight: 800;
  }

  @keyframes attendance-overlay-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes attendance-overlay-out {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }

  @keyframes attendance-backdrop-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes attendance-backdrop-out {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }

  @keyframes attendance-panel-in {
    from {
      opacity: 0;
      transform: translateY(18px) scale(0.985);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  @keyframes attendance-panel-out {
    from {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
    to {
      opacity: 0;
      transform: translateY(12px) scale(0.985);
    }
  }

  @keyframes attendance-alert-in {
    from {
      opacity: 0;
      transform: translateY(10px) scale(0.985);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    &,
    &.is-closing,
    .attendance-backdrop,
    &.is-closing .attendance-backdrop,
    .attendance-panel,
    &.is-closing .attendance-panel,
    .attendance-alert-backdrop,
    .attendance-alert {
      animation: none;
    }

    .attendance-notice-content {
      transition: none;
    }
  }

  @media (max-width: 720px) {
    align-items: center;
    padding: 12px;

    .attendance-panel {
      width: 100%;
      max-height: calc(100vh - 24px);
      border-radius: 16px;
    }

    .attendance-panel-header {
      min-height: 66px;
      padding: 15px;
    }

    h1 {
      font-size: 24px;
    }

    .attendance-title-icon,
    .attendance-close {
      width: 38px;
      height: 38px;
      border-radius: 11px;
    }

    .attendance-panel-body {
      padding: 14px;
      gap: 11px;
    }

    .attendance-status-card {
      align-items: stretch;
      flex-direction: column;
    }

    .attendance-check-button {
      width: 100%;
    }

    .calendar-weekdays,
    .calendar-grid {
      gap: 6px;
    }

    .calendar-day {
      min-height: 36px;
      border-radius: 8px;
      font-size: 12px;
    }

  }
`;

export default AttendanceCheckPage;
